import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import ShareModal from "../components/ShareModal";

export default function Results() {
  const { t, pick } = useI18n();
  const [results, setResults] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [shareQid, setShareQid] = useState(null);

  const load = async () => {
    try {
      const [r, q] = await Promise.all([api.get("/results"), api.get("/questions")]);
      setResults(r.data.results);
      setQuestions(q.data.questions);
      setUpdatedAt(new Date());
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4]" data-testid="results-loading">
        <div className="font-mono-civic text-sm uppercase tracking-widest text-black">{t("results_loading")}</div>
      </div>
    );
  }

  const shareQuestion = shareQid ? questions.find((q) => q.id === shareQid) : null;
  const shareResult = shareQid ? results.find((r) => r.id === shareQid) : null;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black px-5 py-10" data-testid="results-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black flex items-center gap-2">
              <span className="w-2 h-2 bg-[#E00000] rounded-full animate-pulse" />
              {t("results_tag")}
            </div>
            <h1 className="mt-2 font-serif-display text-black leading-[0.9] text-5xl sm:text-6xl" style={{ fontWeight: 800 }}>{t("results_h1")}</h1>
            <p className="mt-3 text-black max-w-2xl" style={{ fontWeight: 700 }}>
              {t("results_intro_a")} <span className="text-[#E00000]" style={{ fontWeight: 900 }}>{t("results_intro_emph")}</span>{t("results_intro_b")}
            </p>
          </div>
          <div className="text-right">
            {updatedAt && (
              <div className="font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
                {t("results_updated")} {updatedAt.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-6 border-2 border-[#E00000] bg-white p-3 text-[#E00000] text-sm" style={{ fontWeight: 800 }} data-testid="results-error">{error}</div>
        )}

        <div className="mt-10 space-y-8">
          {results.map((r, idx) => {
            const text = pick(r, "text");
            return (
              <div key={r.id}
                className={`bg-white border-2 border-black p-6 sm:p-8 ${r.main ? "brutal-shadow-red" : "brutal-shadow-sm"}`}
                data-testid={`result-card-${r.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black" style={{ fontWeight: 900 }}>
                    {t("vote_q_label")} {String(idx + 1).padStart(2, "0")} · {t("results_independent")}
                  </div>
                  {r.main && (
                    <span className="font-mono-civic text-[10px] uppercase tracking-widest bg-[#E00000] text-white px-2 py-1" style={{ fontWeight: 900 }}>
                      {t("vote_headline_tag")}
                    </span>
                  )}
                </div>

                <p className={`font-serif-display text-black leading-[1.08] ${r.main ? "text-2xl sm:text-3xl italic" : "text-xl sm:text-2xl"}`} style={{ fontWeight: 800 }}>
                  {text}
                </p>

                <div className="mt-6">
                  {r.total === 0 ? (
                    <div className="font-mono-civic text-xs uppercase tracking-widest text-black/60 border-2 border-dashed border-black p-4 text-center" style={{ fontWeight: 800 }}>
                      {t("results_waiting")}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="border-2 border-black bg-white p-4 text-center" data-testid={`result-yes-box-${r.id}`}>
                          <div className="font-mono-civic text-[10px] sm:text-xs uppercase tracking-widest text-black" style={{ fontWeight: 900 }}>{t("dash_left_yes")}</div>
                          <div className="mt-2 font-serif-display text-black leading-none text-5xl sm:text-6xl" style={{ fontWeight: 800 }}>
                            {r.yes.toLocaleString()}
                          </div>
                          <div className="mt-2 inline-block bg-black text-white font-mono-civic text-[10px] uppercase tracking-widest px-2 py-0.5" style={{ fontWeight: 900 }}>
                            {t("q_yes")} · {r.yes_pct}%
                          </div>
                        </div>
                        <div className="border-2 border-black bg-white p-4 text-center" data-testid={`result-no-box-${r.id}`}>
                          <div className="font-mono-civic text-[10px] sm:text-xs uppercase tracking-widest text-black" style={{ fontWeight: 900 }}>{t("dash_right_no")}</div>
                          <div className="mt-2 font-serif-display text-[#E00000] leading-none text-5xl sm:text-6xl" style={{ fontWeight: 800 }}>
                            {r.no.toLocaleString()}
                          </div>
                          <div className="mt-2 inline-block bg-[#E00000] text-white font-mono-civic text-[10px] uppercase tracking-widest px-2 py-0.5" style={{ fontWeight: 900 }}>
                            {t("q_no")} · {r.no_pct}%
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 border-2 border-black bg-white">
                        <div className="flex h-6">
                          <div className="bg-black" style={{ width: `${r.yes_pct}%` }} />
                          <div className="bg-[#E00000]" style={{ width: `${r.no_pct}%` }} />
                        </div>
                      </div>
                      <div className="mt-3 font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 800 }}>
                        {r.total.toLocaleString()} {t("results_votes_on")} · {r.yes_pct >= r.no_pct ? t("results_yes_lead") : t("results_no_lead")}
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link to={`/vote/${r.id}`}
                    className="bg-white text-black uppercase tracking-widest py-3 px-4 border-2 border-black hover:bg-black hover:text-white transition-colors text-center text-sm"
                    style={{ fontWeight: 800 }} data-testid={`result-open-${r.id}`}>
                    {t("results_open_q")}
                  </Link>
                  <button onClick={() => setShareQid(r.id)} disabled={r.total === 0}
                    className="bg-[#E00000] text-white uppercase tracking-widest py-3 px-4 border-2 border-[#E00000] hover:bg-white hover:text-[#E00000] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontWeight: 900 }} data-testid={`result-share-${r.id}`}>
                    {t("results_share")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <ShareModal open={!!shareQid} onClose={() => setShareQid(null)} question={shareQuestion} result={shareResult} />

        {/* Discussion now lives on its own page so it can host thousands of comments
            without crowding the live tally.  Linking out keeps Results focused. */}
        <Link
          to="/discussion"
          className="mt-12 block bg-white border-2 border-black brutal-shadow p-6 sm:p-8 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          data-testid="results-to-discussion"
        >
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 bg-[#E00000] text-white items-center justify-center font-serif-display text-3xl shrink-0" style={{ fontWeight: 800 }}>
              ”
            </div>
            <div className="flex-1">
              <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000]" style={{ fontWeight: 900 }}>
                {t("results_discussion_tag")}
              </div>
              <div className="mt-1 font-serif-display text-2xl sm:text-3xl text-black leading-tight" style={{ fontWeight: 800 }}>
                {t("results_discussion_h")}
              </div>
              <div className="mt-2 text-sm text-black" style={{ fontWeight: 600 }}>
                {t("results_discussion_p")}
              </div>
            </div>
            <div className="hidden sm:block font-mono-civic text-xs uppercase tracking-widest text-black self-center" style={{ fontWeight: 900 }}>
              {t("results_discussion_cta")} →
            </div>
          </div>
        </Link>

        <div className="mt-12 border-t-2 border-black pt-8 flex flex-col sm:flex-row gap-4">
          <Link to="/dashboard"
            className="inline-flex items-center justify-center bg-black text-white uppercase tracking-widest px-8 py-5 border-2 border-black brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex-1 sm:flex-initial"
            style={{ fontWeight: 800 }} data-testid="results-to-dashboard-btn">
            {t("results_back_dash")}
          </Link>
          <Link to="/vote"
            className="inline-flex items-center justify-center bg-white text-black uppercase tracking-widest px-8 py-5 border-2 border-black hover:bg-black hover:text-white transition-colors flex-1 sm:flex-initial"
            style={{ fontWeight: 800 }}>
            {t("results_cast")}
          </Link>
        </div>
      </div>
    </div>
  );
}
